import { MenuItem } from './MenuItem';
import profileImage from '@/assets/icons/babyshark.png';
import HomeIcon from '@/assets/icons/home.svg';
import MovieIcon from '@/assets/icons/movie.svg';
import MusicIcon from '@/assets/icons/music.svg';
import StoryIcon from '@/assets/icons/story.svg';
import TopicIcon from '@/assets/icons/topic.svg';
import { Device, Sound } from '@/classes';
import { keyCodes } from '@/constants/keyCode';
import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { useFocusElement } from '@/hooks/useFocusElement';
import { useRouter } from '@/hooks/useRouter';
import { focusNearestVisibleContent, MENU_FOCUS_KEY, MENU_ROOT_ATTRIBUTE } from '@/utils';
import { FocusContext } from '@noriginmedia/norigin-spatial-navigation';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const myPageFontSizeMap: Record<string, string> = {
  ja: '22px',
  zh: '26px',
};

const FocusableMyPageButton = ({ onFocus }: { onFocus: () => void }) => {
  const location = useLocation();
  const { ref, focused, focusSelf } = useExtendFocusable({
    onFocus: onFocus,
    // 우측 키로 콘텐츠에 진입할 때는 예전 포커스를 복원하지 않고 화면에 보이는 항목으로 이동시킨다.
    onArrowPress: (direction) => (direction === 'right' ? focusNearestVisibleContent() : true),
  });
  const { t } = useTranslation();
  const currentPath = location.pathname;
  const isActive = currentPath === '/profile';
  const fontSize = myPageFontSizeMap[Device.language] ?? '1.5rem';
  const handleClick = useCallback(() => {
    Sound.press();
    if (focusSelf) {
      focusSelf();
    }
  }, [focusSelf]);

  useFocusElement({ ref, focused, onClick: onFocus, keyCode: keyCodes.ENTER });

  useEffect(() => {
    if (isActive) {
      focusSelf();
    }
  }, [location, focusSelf, isActive]);

  return (
    <div
      className={`flex flex-col items-center justify-center w-full mb-8`}
      onClick={handleClick}
      onContextMenu={handleClick}
    >
      <img
        ref={ref}
        tabIndex={0}
        className={`w-[112px] h-[112px] p-[6px] rounded-[20px] outline-disable outline-secondary-8`}
        src={profileImage}
        alt="profile"
      />
      <span className="p-2 font-medium text-black" style={{ fontSize: fontSize }}>
        {t('menu_my')}
      </span>
    </div>
  );
};

export const Menu = ({ hidden = false }: { hidden?: boolean }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const router = useRouter();
  const { ref, focusKey } = useExtendFocusable({
    isContainer: true,
    focusable: !hidden,
    trackChildren: true,
    saveLastFocusedChild: true,
    isFocusBoundary: true,
    focusBoundaryDirections: ['up', 'down', 'left'],
    focusKey: MENU_FOCUS_KEY,
  });
  const fontSize = ['zh', 'ja'].includes(Device.language) ? '26px' : '30px';

  const navigateTo = useCallback(
    (path: string) => {
      if (location.pathname.includes(path)) return;

      router.push(path, { replace: true });
    },
    [router, location.pathname],
  );

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        {...{ [MENU_ROOT_ATTRIBUTE]: '' }}
        className="relative font-babyshark flex flex-col w-30 h-screen px-4 py-10 box-shadow-lg z-50"
        style={{ background: '#FFFDF1', display: hidden ? 'none' : 'block' }}
        ref={ref}
      >
        <FocusableMyPageButton onFocus={() => navigateTo('/profile')} />
        <nav className="flex-col mt-[45px] pb-6">
          <MenuItem
            fontSize={fontSize}
            path="home"
            title={t('menu_home')}
            icon={<img className="w-[60px] h-[60px]" src={HomeIcon} />}
            onFocus={() => navigateTo('/home')}
          />
          <MenuItem
            fontSize={fontSize}
            path="nursery-rhymes"
            title={t('menu_song')}
            icon={<img className="w-[60px] h-[60px]" src={MusicIcon} />}
            onFocus={() => {
              navigateTo('/nursery-rhymes');
            }}
          />
          <MenuItem
            fontSize={fontSize}
            path="fairy-tales"
            title={t('menu_story')}
            icon={<img className="w-[60px] h-[60px]" src={StoryIcon} />}
            onFocus={() => navigateTo('/fairy-tales')}
          />
          <MenuItem
            fontSize={fontSize}
            path="movies"
            title={t('menu_cinema')}
            icon={<img className="w-[60px] h-[60px]" src={MovieIcon} />}
            onFocus={() => {
              navigateTo('/movies');
            }}
          />
          <MenuItem
            fontSize={fontSize}
            path="topics"
            title={t('menu_topics')}
            icon={<img className="w-[60px] h-[60px]" src={TopicIcon} />}
            onFocus={() => navigateTo('/topics')}
          />
        </nav>
      </div>
    </FocusContext.Provider>
  );
};
